import {
  ENTITLEMENT_LABELS,
  EntitlementKey,
  PLAN_CODE_BUSINESS,
  PLAN_CODE_PROFESSIONAL,
} from './entitlement-keys';
import { TRACKING_MODES } from '../utils/tracking-mode-labels.util';

export type SoftwareVersionType = 'silent' | 'general' | 'restricted';

export type SoftwareTypeFact = {
  icon: string;
  text: string;
};

export type SoftwareTypeOption = {
  value: SoftwareVersionType;
  label: string;
  description: string;
  tooltip: string;
  locked: boolean;
  /** Material icon name for the mode card. */
  icon: string;
  /** Highlight Manual as the everyday default (Krug: don't force a cold decision). */
  recommended: boolean;
  /** Short caution under Hidden only; empty for other modes. */
  caution: string;
  /** Max two scannable facts — who starts it / visible? */
  facts: SoftwareTypeFact[];
};

/** Create-employee / Collection-aligned fact lines — icon visibility, not vague "on their computer". */
const MODE_FACT_SELF_START: SoftwareTypeFact = {
  icon: 'touch_app',
  text: 'Employee starts and stops tracking',
};
const MODE_FACT_AUTO_START: SoftwareTypeFact = {
  icon: 'autorenew',
  text: 'Tracking starts automatically',
};
const MODE_FACT_ICON_VISIBLE: SoftwareTypeFact = {
  icon: 'visibility',
  text: 'SuperSee icon is visible',
};
const MODE_FACT_ICON_HIDDEN: SoftwareTypeFact = {
  icon: 'visibility_off',
  text: 'SuperSee icon is hidden',
};
const MODE_HIDDEN_CAUTION =
  'Employee won’t see monitoring. Make sure this complies with your local laws and company policy.';

const SOFTWARE_TYPE_COPY: Record<SoftwareVersionType, Omit<SoftwareTypeOption, 'value' | 'locked'>> = {
  silent: {
    label: TRACKING_MODES.hidden.label,
    description: TRACKING_MODES.hidden.summary,
    tooltip: TRACKING_MODES.hidden.summary,
    icon: 'visibility_off',
    recommended: false,
    caution: MODE_HIDDEN_CAUTION,
    facts: [MODE_FACT_AUTO_START, MODE_FACT_ICON_HIDDEN],
  },
  general: {
    label: TRACKING_MODES.employee_controlled.label,
    description: TRACKING_MODES.employee_controlled.summary,
    tooltip: TRACKING_MODES.employee_controlled.summary,
    icon: 'touch_app',
    recommended: true,
    caution: '',
    facts: [MODE_FACT_SELF_START, MODE_FACT_ICON_VISIBLE],
  },
  restricted: {
    label: TRACKING_MODES.admin_controlled.label,
    description: TRACKING_MODES.admin_controlled.summary,
    tooltip: TRACKING_MODES.admin_controlled.summary,
    icon: 'autorenew',
    recommended: false,
    caution: '',
    facts: [MODE_FACT_AUTO_START, MODE_FACT_ICON_VISIBLE],
  },
};

const SOFTWARE_TYPE_ORDER: SoftwareVersionType[] = ['general', 'restricted', 'silent'];

function toOption(
  value: SoftwareVersionType,
  locked: boolean
): SoftwareTypeOption {
  return { value, locked, ...SOFTWARE_TYPE_COPY[value] };
}

/** Hidden + Admin Controlled: Basic locked; Professional/Business unlocked. */
export function canUseHiddenSoftwareModes(
  planCode: string | null,
  enforcementActive: boolean,
  hasHiddenModeEntitlement: boolean
): boolean {
  if (!enforcementActive) {
    return true;
  }
  if (hasHiddenModeEntitlement) {
    return true;
  }
  const code = String(planCode ?? '').toLowerCase();
  return code === PLAN_CODE_PROFESSIONAL || code === PLAN_CODE_BUSINESS;
}

export function isSoftwareVersionTypeLocked(
  versionType: string,
  planCode: string | null,
  enforcementActive: boolean,
  hasHiddenModeEntitlement: boolean
): boolean {
  return (
    validateSoftwareVersionType(
      versionType,
      planCode,
      enforcementActive,
      hasHiddenModeEntitlement
    ) !== null
  );
}

/**
 * All software types for create-employee UI. Locked options stay visible but are not selectable.
 */
export function getSoftwareTypeOptions(
  planCode: string | null,
  enforcementActive: boolean,
  hasHiddenModeEntitlement: boolean
): SoftwareTypeOption[] {
  return SOFTWARE_TYPE_ORDER.map((value) =>
    toOption(
      value,
      isSoftwareVersionTypeLocked(
        value,
        planCode,
        enforcementActive,
        hasHiddenModeEntitlement
      )
    )
  );
}

export function getDefaultSoftwareVersionType(
  options: SoftwareTypeOption[]
): SoftwareVersionType | null {
  const unlocked = options.filter((option) => !option.locked);
  if (!unlocked.length) {
    return null;
  }
  return unlocked.find((option) => option.value === 'general')?.value ?? unlocked[0].value;
}

/**
 * Map the activation journey's default mode to a create-employee software type.
 * Hidden (app off) is the workspace default only when the founder chose it, or the journey mode is silent.
 */
export function resolveActivationHandoffVersionType(state: {
  mode?: string | null;
  pilotEmployeeControlled?: boolean;
  pilotTrayOff?: boolean;
}): SoftwareVersionType {
  const mode = String(state?.mode || '').toLowerCase();
  if (mode === 'silent' || state?.pilotTrayOff === true) {
    return 'silent';
  }
  if (state?.pilotEmployeeControlled !== false) {
    return 'general';
  }
  return 'restricted';
}

export function validateSoftwareVersionType(
  versionType: string,
  planCode: string | null,
  enforcementActive: boolean,
  hasHiddenModeEntitlement: boolean
): string | null {
  const normalized = String(versionType || '').toLowerCase() as SoftwareVersionType;
  if (!SOFTWARE_TYPE_ORDER.includes(normalized)) {
    return 'Select a software type to install.';
  }
  if (!enforcementActive) {
    return null;
  }
  // Employee Controlled is the baseline mode on every tier (Basic included).
  if (normalized === 'general') {
    return null;
  }
  if (
    (normalized === 'silent' || normalized === 'restricted')
    && !canUseHiddenSoftwareModes(planCode, enforcementActive, hasHiddenModeEntitlement)
  ) {
    return 'Hidden and Automatic modes require a Professional plan or higher.';
  }
  return null;
}

export function hiddenModeUpgradeMessage(): string {
  const label = ENTITLEMENT_LABELS[EntitlementKey.hidden_mode] ?? 'Hidden monitoring mode';
  return `${label} is included on Professional and Business plans.`;
}

export type OnboardingTrackingModeId = 'hidden' | 'employee_controlled' | 'admin_controlled';

export function isOnboardingTrackingModeAllowed(
  modeId: string,
  planCode: string | null,
  enforcementActive: boolean,
  hasHiddenModeEntitlement: boolean
): boolean {
  const normalized = String(modeId || '').toLowerCase();
  if (!enforcementActive) {
    return normalized === 'hidden' || normalized === 'employee_controlled' || normalized === 'admin_controlled';
  }
  if (normalized === 'employee_controlled') {
    return true;
  }
  if (normalized === 'hidden' || normalized === 'admin_controlled') {
    return canUseHiddenSoftwareModes(planCode, enforcementActive, hasHiddenModeEntitlement);
  }
  return false;
}
